import React, { useRef, useEffect, useCallback } from 'react';
import { ITurnstileProps } from '../../../types/humanVerification';
import { useHumanVerification } from '../../../hooks/useHumanVerification';
import { trackExceptionWithContext } from '../../../services/application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from '../../../services/application-insights/appInsightsConstants';

declare global {
	interface Window {
		turnstile?: {
			render: (
				container: HTMLElement | string,
				options: {
					sitekey: string;
					callback: (token: string) => void;
					'error-callback'?: (error: string) => void;
					size?: 'normal' | 'flexible' | 'compact' | 'invisible';
					appearance?: 'always' | 'execute' | 'interaction-only';
				},
			) => string;
			reset: (widgetId?: string) => void;
			remove: (widgetId?: string) => void;
		};
	}
}

export const TurnstileWidget: React.FC<ITurnstileProps> = ({
	siteKey,
	action,
	size = 'normal',
	appearance = 'always',
	onVerified,
	onError,
	onExpired,
	onLoad,
	className,
	...props
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<string | null>(null);
	const scriptLoadedRef = useRef(false);

	const { verify, isLoading } = useHumanVerification({
		provider: 'turnstile',
		action,
		onVerified,
		onError,
	});

	const handleVerification = useCallback(
		async (token: string) => {
			try {
				if (isLoading) return;

				const result = await verify(token);

				if (result.isSuccess) {
					onVerified?.(token, result);
				} else {
					const errorMessage = result.errorMessage || 'Verification failed';
					trackExceptionWithContext(new Error(errorMessage), {
						component: AppInsightsComponents.TURNSTILE_WIDGET,
						action: 'verification-failed',
						additionalData: {
							errorType: AppInsightsErrorTypes.TURNSTILE_VERIFICATION_ERROR,
							hasToken: !!token,
							tokenLength: token?.length || 0,
							action
						}
					});
					onError?.(errorMessage);
				}
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.TURNSTILE_WIDGET,
					action: 'verification-exception',
					additionalData: {
						errorType: AppInsightsErrorTypes.TURNSTILE_VERIFICATION_ERROR,
						hasToken: !!token,
						action
					}
				});
				onError?.(error instanceof Error ? error.message : 'Verification failed');
			}
		},
		[verify, onVerified, onError, isLoading, action],
	);

	const handleError = useCallback(
		(error: string) => {
			console.error('Turnstile error:', error);
			
			trackExceptionWithContext(new Error(`Turnstile widget error: ${error}`), {
				component: AppInsightsComponents.TURNSTILE_WIDGET,
				action: 'widget-error',
				additionalData: {
					errorType: AppInsightsErrorTypes.TURNSTILE_WIDGET_ERROR,
					errorMessage: error,
					action,
					siteKey: siteKey?.substring(0, 10) + '...' // Log partial site key for debugging
				}
			});
			
			onError?.(error);
		},
		[onError, action, siteKey],
	);

	const loadTurnstileScript = useCallback(() => {
		if (scriptLoadedRef.current || document.querySelector('script[src*="turnstile"]')) {
			return Promise.resolve();
		}

		return new Promise<void>((resolve, reject) => {
			const script = document.createElement('script');
			script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
			script.async = true;
			script.defer = true;

			script.onload = () => {
				scriptLoadedRef.current = true;
				onLoad?.();
				resolve();
			};

			script.onerror = () => {
				const error = new Error('Failed to load Turnstile script');
				trackExceptionWithContext(error, {
					component: AppInsightsComponents.TURNSTILE_WIDGET,
					action: 'script-load-failed',
					additionalData: {
						errorType: AppInsightsErrorTypes.TURNSTILE_SCRIPT_LOAD_ERROR,
						scriptUrl: 'https://challenges.cloudflare.com/turnstile/v0/api.js',
						action,
						siteKey: siteKey?.substring(0, 10) + '...'
					}
				});
				reject(error);
			};

			document.head.appendChild(script);
		});
	}, [onLoad, action, siteKey]);

	const renderWidget = useCallback(() => {
		if (!containerRef.current || !window.turnstile) {
			return;
		}

		try {
			widgetIdRef.current = window.turnstile.render(containerRef.current, {
				sitekey: siteKey,
				callback: handleVerification,
				'error-callback': handleError,
				size,
				appearance,
			});
		} catch (error) {
			console.error('Failed to render Turnstile widget:', error);
			
			trackExceptionWithContext(error as Error, {
				component: AppInsightsComponents.TURNSTILE_WIDGET,
				action: 'render-failed',
				additionalData: {
					errorType: AppInsightsErrorTypes.TURNSTILE_RENDER_ERROR,
					hasContainer: !!containerRef.current,
					hasTurnstile: !!window.turnstile,
					size,
					appearance,
					action,
					siteKey: siteKey?.substring(0, 10) + '...'
				}
			});
			
			onError?.(error instanceof Error ? error.message : 'Failed to render widget');
		}
	}, [siteKey, handleVerification, handleError, size, appearance, onError, action]);

	const resetWidget = useCallback(() => {
		if (window.turnstile && widgetIdRef.current) {
			window.turnstile.reset(widgetIdRef.current);
		}
	}, []);

	const removeWidget = useCallback(() => {
		if (window.turnstile && widgetIdRef.current) {
			window.turnstile.remove(widgetIdRef.current);
			widgetIdRef.current = null;
		}
	}, []);

	useEffect(() => {
		const initializeWidget = async () => {
			try {
				await loadTurnstileScript();
				renderWidget();
			} catch (error) {
				console.error('Failed to initialize Turnstile:', error);
				
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.TURNSTILE_WIDGET,
					action: 'initialization-failed',
					additionalData: {
						errorType: AppInsightsErrorTypes.TURNSTILE_WIDGET_ERROR,
						action,
						siteKey: siteKey?.substring(0, 10) + '...',
						size,
						appearance
					}
				});
				
				onError?.(error instanceof Error ? error.message : 'Failed to initialize widget');
			}
		};

		initializeWidget();

		return () => {
			removeWidget();
		};
	}, [loadTurnstileScript, renderWidget, removeWidget, onError]);

	// Handle expired tokens
	useEffect(() => {
		const handleExpiration = () => {
			onExpired?.();
			resetWidget();
		};

		// Listen for visibility changes to handle potential token expiration
		const handleVisibilityChange = () => {
			if (document.hidden) {
				// Page is hidden, token might expire
				setTimeout(() => {
					if (!document.hidden) {
						handleExpiration();
					}
				}, 60000); // Check after 1 minute
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [onExpired, resetWidget]);

	return (
		<div className={`turnstile-widget ${className || ''}`} {...props}>
			<div ref={containerRef} className={`turnstile-container ${isLoading ? 'opacity-50' : ''}`} />
			{isLoading && (
				<div className="absolute inset-0 flex items-center justify-center bg-white/50">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
				</div>
			)}
		</div>
	);
};

export default TurnstileWidget;
